import { IMPRESSION_TYPE } from '../../hooks/DiffRetainerHooks/useRetainerRequestForm';
import {
    CaseImpression,
    CaseImpressionDTO,
    CaseImpressionFileType,
    RetainerRequestFormValues,
    StoreFile,
    StoreFilesValue,
} from './type';
import axios from '../../../axios-default';
import { getFormKeysArray } from '../../../components/DifferentRetainer/Question';
import { initRetainerReqFormValue } from '../../../store/DiffRetainers/reducer';
import { ENABLE_OCC_BITE_RAMP_QUESTION } from '../utils';
import { PLATFORM_TYPE } from '../../../model/oauth/type';
export default class RetainerRequestHandler {
    static UPPER_WIRE_SELECTION_FIELDS = [
        'upperPalatalWireSelect',
        'upperSmartFitWireSelect',
        'upperEcodontWireSelect',
    ];
    static LOWER_WIRE_SELECTION_FIELDS = [
        'lowerPalatalWireSelect',
        'lowerSmartFitWireSelect',
        'lowerEcodontWireSelect',
    ];
    static COMBINE_WIRE_SELECTION_FIELDS = [...this.UPPER_WIRE_SELECTION_FIELDS, ...this.LOWER_WIRE_SELECTION_FIELDS];

    static TEETH_SELECTION_FIELDS = ['upperPontics', 'upperBiteRamp', 'lowerPontics', 'lowerBiteRamp'];

    /**
     * Constructs a new RetainerRequestHandler object.
     *
     * @constructor
     * @param {Object} options - The options for the RetainerRequestHandler.
     * @param {IMPRESSION_TYPE} options.impressionType - The type of impression.
     * @param {number} [options.caseId=null] - The ID of the case, or null if there is no case.
     * @throws {Error} If no impressionType is provided.
     */
    constructor({ impressionType, caseId, isReSubmit }) {
        if (!impressionType) throw new Error('impressionType is required');
        this.impressionType = impressionType;
        this.caseId = caseId ? caseId : null;
        this.isNewCase = caseId ? false : true;
        this.isReSubmit = isReSubmit ? true : false;
    }

    /**
     * @type {(data:Object , storeFilesValue:StoreFilesValue) => FormData}
     */
    handleRetainerRequest(data, storeFilesValue) {
        if (!data) throw new Error('data is required');
        if (!storeFilesValue) throw new Error('storeFilesValue is required');
        const [formData, caseImpression] = this.handleFormData(storeFilesValue, data);
        return this.handleRetainerRequestFormData(data, formData, caseImpression);
    }

    /**
     * @type {(data:Object , formData:FormData, caseImpression:CaseImpression) => FormData}
     */
    handleRetainerRequestFormData(data, formData, caseImpression) {
        const formObj = this.getRetainerRequestFormValues(data);

        // console.log('formObj = >' , JSON.stringify(formObj , null , 4))
        // console.log('caseImpression = >' , JSON.stringify(caseImpression , null , 4))
        // console.log(JSON.stringify({
        //     case_impression : caseImpression,
        //     retainer_request : formObj
        // } , null , 4))

        formData.append(
            'body',
            JSON.stringify({
                case_impression: caseImpression,
                retainer_request: formObj,
            })
        );
        return formData;
    }

    /**
     * @type {(storeFilesValue:StoreFilesValue, data:any) => FormData}
     */
    handleFormData(storeFilesValue, data) {
        if (!storeFilesValue) throw new Error('storeFilesValue is required');
        const baseCaseImpression = this.getBaseCaseImpression();
        const formData = new FormData();
        if (storeFilesValue.files.length > 0) {
            storeFilesValue.files.forEach((file, i) => {
                const fileName = '$$FILE:stl_file' + i.toString();
                baseCaseImpression[`stl_file${i}`] = file.data ? fileName : null;
                if (file.data) {
                    formData.append(fileName, file.data);
                }
            });
        }
        const { modernCode, waybillNumber, courierCompany, scannerBrand, doctorId } = storeFilesValue;
        baseCaseImpression.modern_code = modernCode;
        baseCaseImpression.waybill_number = waybillNumber;
        baseCaseImpression.courier_company = courierCompany;
        baseCaseImpression.scanner_brand = scannerBrand;
        baseCaseImpression.doctor_id = doctorId;
        baseCaseImpression.type = this.getCaseImpressionType(storeFilesValue, data);
        return [formData, baseCaseImpression];
    }

    async createRetainerRequestWithCase(data, onUploadProgress) {
        if (!this.caseId) throw new Error('caseId is required');
        const res = await axios.post(`case/retainer/${this.caseId}`, data, { onUploadProgress });
        return res;
    }

    async resubmitRetainerRequestWithCaseId(data, caseId, caseImpressionId, retainerRequestId, onUploadProgress) {
        if (!caseId) throw new Error('caseId is required');
        if (!caseImpressionId) throw new Error('caseImpressionId is required');
        let retainerRequsetParam = 0;
        if (retainerRequestId) {
            retainerRequsetParam = retainerRequestId;
        }
        const res = await axios.post(
            `case/retainer/resubmit/${caseId}/caseImpression/${caseImpressionId}/retainerRequest/${retainerRequsetParam}`,
            data,
            { onUploadProgress }
        );
        return res;
    }

    /**
     * @type {() => CaseImpressionDTO}
     */
    getBaseCaseImpression() {
        return {
            type: '',
            caseId: this.caseId,
            stl_file0: '',
            stl_file1: '',
            stl_file2: '',
            stl_file3: '',
            modern_code: '',
            waybill_number: '',
            courier_company: '',
            scanner_brand: '',
            doctor_id: '',
        };
    }

    /**
     * @type {(storeFilesValue:StoreFilesValue, data:any) => CaseImpressionFileType | ""}
     */
    getCaseImpressionType(storeFilesValue, data) {
        if (this.isNewCase || this.isReSubmit) return '';
        const { impressionOption } = data;
        if (impressionOption === CaseImpressionFileType.LATEST_APPROVED_TREATMENT_PLAN)
            return CaseImpressionFileType.LATEST_APPROVED_TREATMENT_PLAN;
        if (impressionOption === CaseImpressionFileType.LATEST_RETAINER_REQUEST)
            return CaseImpressionFileType.LATEST_RETAINER_REQUEST;
        if (impressionOption === CaseImpressionFileType.FILE) {
            const { files, modernCode, waybillNumber, courierCompany, scannerBrand, doctorId } = storeFilesValue;
            if (modernCode) return CaseImpressionFileType.COURIER;
            if (scannerBrand && doctorId) return CaseImpressionFileType.SCANNER;
            const isFilesValid = this.validateFiles(files);
            if (!isFilesValid)
                throw new Error('Please upload required impression files or fill in the necessary information');
            if (isFilesValid) return CaseImpressionFileType.FILE;
        }
        throw new Error('Invalid case impression type');
    }

    /**
     * @type {(data:any) => RetainerRequestFormValues}
     */
    getRetainerRequestFormValues(data) {
        if (!data) throw new Error('data is required');
        if (!this.impressionType) throw new Error('impressionType is required');
        const baseRetainerRequestFormValues = new Map([
            ['impressionType', this.impressionType],
            ['upperFixedWire', null],
            ['upperArchAndQuantity', null],
            ['upperBleachingRetainersSelect', null],
            // ['upperFixedPalatalWireSelect' , null],
            ['upperWireSelect', null],
            ['upperJigPlacementSelect', null],
            ['upperOcclusalBiteRampQuestion', null],
            ['upperLowTrimlineAreaSelect_3', null],
            ['upperLowTrimlineAreaSelect_1', null],
            ['upperClearRetainerDesignUpper', null],
            ['upperClearRetainerDesignUpperOption', null],
            // ['upperClearRetainerDesignLower' , null],
            // ['upperClearRetainerDesignLowerOption' , null],
            ['upperClearRetainerDesignUpperForSecondSet', null],
            ['upperClearRetainerDesignUpperOptionForSecondSet', null],
            // ['upperClearRetainerDesignLowerForSecondSet' , null],
            // ['upperClearRetainerDesignLowerOptionForSecondSet' , null],
            ['upperPontics', null],
            ['upperBiteRamp', null],
            ['upperPontic', null],
            ['upperPonticColor', null],

            ['lowerFixedWire', null],
            ['lowerArchAndQuantity', null],
            ['lowerBleachingRetainersSelect', null],
            // ['lowerFixedPalatalWireSelect' , null],
            ['lowerWireSelect', null],
            ['lowerJigPlacementSelect', null],
            ['lowerLowTrimlineAreaSelect_3', null],
            ['lowerLowTrimlineAreaSelect_1', null],
            ['lowerOcclusalBiteRampQuestion', null],
            // ['lowerClearRetainerDesignUpper' , null],
            // ['lowerClearRetainerDesignUpperOption' , null],
            ['lowerClearRetainerDesignLower', null],
            ['lowerClearRetainerDesignLowerOption', null],
            // ['lowerClearRetainerDesignUpperForSecondSet' , null],
            // ['lowerClearRetainerDesignUpperOptionForSecondSet' , null],
            ['lowerClearRetainerDesignLowerForSecondSet', null],
            ['lowerClearRetainerDesignLowerOptionForSecondSet', null],
            ['lowerPontics', null],
            ['lowerBiteRamp', null],
            ['lowerPontic', null],
            ['lowerPonticColor', null],

            ['specialInstruction', null],
        ]);

        const keys = getFormKeysArray(this.impressionType);

        switch (this.impressionType) {
            case IMPRESSION_TYPE.UPPER:
                keys.forEach((key) => {
                    const prefixKey = `upper${key.charAt(0).toUpperCase() + key.slice(1)}`;
                    const isTeethSelectionKey = ['upperPontics', 'biteRamp'].includes(prefixKey);
                    const isWireSelectionKey = RetainerRequestHandler.UPPER_WIRE_SELECTION_FIELDS.includes(prefixKey);
                    if (isTeethSelectionKey) {
                        baseRetainerRequestFormValues.set(prefixKey, Array.isArray(data[key]) ? data[key] : null);
                        const retainerTypeField = data['archAndQuantity'];
                        if (!ENABLE_OCC_BITE_RAMP_QUESTION.includes(retainerTypeField) && prefixKey === 'biteRamp') {
                            baseRetainerRequestFormValues.set(prefixKey, []);
                        }
                        return;
                    }
                    // below condition means handling wire select key and the value is not set yet
                    // because in backend will use same column (upper_wire_select) for different wire select value
                    if (isWireSelectionKey) {
                        if (baseRetainerRequestFormValues.get('upperWireSelect') === null) {
                            const retainerTypeField = data['archAndQuantity'];
                            const targetSelectValue = this.handleWireSelectValue(data, retainerTypeField);
                            baseRetainerRequestFormValues.set('upperWireSelect', targetSelectValue);
                        }
                        return;
                    }
                    // not adding value to form if the key is not exist in baseRetainerRequestFormValues
                    const prefixKeyValue = baseRetainerRequestFormValues.get(prefixKey);
                    if (prefixKeyValue === undefined) return;
                    baseRetainerRequestFormValues.set(prefixKey, data[key] !== undefined ? data[key] : null);
                });
                break;
            case IMPRESSION_TYPE.LOWER:
                keys.forEach((key) => {
                    const prefixKey = `lower${key.charAt(0).toUpperCase() + key.slice(1)}`;
                    const isTeethSelectionKey = ['lowerPontics', 'biteRamp'].includes(prefixKey);
                    const isWireSelectionKey = RetainerRequestHandler.LOWER_WIRE_SELECTION_FIELDS.includes(prefixKey);
                    if (isTeethSelectionKey) {
                        baseRetainerRequestFormValues.set(prefixKey, Array.isArray(data[key]) ? data[key] : null);
                        const retainerTypeField = data['archAndQuantity'];
                        if (!ENABLE_OCC_BITE_RAMP_QUESTION.includes(retainerTypeField) && prefixKey === 'biteRamp') {
                            baseRetainerRequestFormValues.set(prefixKey, []);
                        }
                        return;
                    }
                    // below condition means handling wire select key and the value is not set yet
                    // because in backend will use same column (lower_wire_select) for different wire select value
                    if (isWireSelectionKey) {
                        if (baseRetainerRequestFormValues.get('lowerWireSelect') === null) {
                            const retainerTypeField = data['archAndQuantity'];
                            const targetSelectValue = this.handleWireSelectValue(data, retainerTypeField);
                            baseRetainerRequestFormValues.set('lowerWireSelect', targetSelectValue);
                        }
                        return;
                    }
                    // not adding value to form if the key is not exist in baseRetainerRequestFormValues
                    const prefixKeyValue = baseRetainerRequestFormValues.get(prefixKey);
                    if (prefixKeyValue === undefined) return;
                    baseRetainerRequestFormValues.set(prefixKey, data[key] !== undefined ? data[key] : null);
                });
                break;
            case IMPRESSION_TYPE.UPPER_AND_LOWER:
                keys.forEach((key) => {
                    const prefixKey = `${key.charAt(0).toLowerCase() + key.slice(1)}`;
                    const isTeethSelectionKey = [
                        'upperPontics',
                        'upperBiteRamp',
                        'lowerPontics',
                        'lowerBiteRamp',
                    ].includes(prefixKey);
                    const isWireSelectionKey = RetainerRequestHandler.COMBINE_WIRE_SELECTION_FIELDS.includes(prefixKey);
                    if (isTeethSelectionKey) {
                        baseRetainerRequestFormValues.set(
                            prefixKey,
                            Array.isArray(data[prefixKey]) ? data[prefixKey] : null
                        );
                        return;
                    }
                    if (isWireSelectionKey) {
                        const isUpper = prefixKey.includes('upper');
                        const dataTargetField = `${isUpper ? 'Upper' : 'Lower'}ArchAndQuantity`; // key for rerieving retainer type value from form data
                        const mapTargetField = `${isUpper ? 'upper' : 'lower'}WireSelect`; // key for mapping wire select value
                        if (baseRetainerRequestFormValues.get(mapTargetField) === null) {
                            const retainerTypeField = data[dataTargetField];
                            const targetSelectValue = this.handleWireSelectValue(
                                data,
                                retainerTypeField,
                                isUpper ? 'Upper' : 'Lower'
                            );
                            baseRetainerRequestFormValues.set(mapTargetField, targetSelectValue);
                        }
                        return;
                    }
                    // not adding value to form if the key is not exist in baseRetainerRequestFormValues
                    const prefixKeyValue = baseRetainerRequestFormValues.get(prefixKey);
                    if (prefixKeyValue === undefined) return;
                    baseRetainerRequestFormValues.set(prefixKey, data[key] !== undefined ? data[key] : null);
                });
                break;
            default:
                break;
        }
        baseRetainerRequestFormValues.set('specialInstruction', data.specialInstruction || null);
        return Object.fromEntries(baseRetainerRequestFormValues);
    }

    handleSingleOffTypeRetainerRequestFormValues(data) {
        const oppositeImpressionType =
            this.impressionType === IMPRESSION_TYPE.UPPER ? IMPRESSION_TYPE.LOWER : IMPRESSION_TYPE.UPPER;
        data[`${oppositeImpressionType}_retainer_type_id`] = null;
        data[`${oppositeImpressionType}_fixed_wire`] = null;
        data[`${oppositeImpressionType}_bleaching_retainers_select`] = null;
        data[`${oppositeImpressionType}_wire_select`] = null;
        data[`${oppositeImpressionType}_jig_placement_select`] = null;
        data[`${oppositeImpressionType}_pontic`] = null;
        data[`${oppositeImpressionType}_pontic_color`] = null;
        data[`${oppositeImpressionType}_bite_ramp`] = [];
        data[`${oppositeImpressionType}_clear_retainer_design_upper_option`] = null;
        data[`${oppositeImpressionType}_clear_retainer_design_upper_option_for_second_set`] = null;
        data[`${oppositeImpressionType}_clear_retainer_design_lower_option`] = null;
        data[`${oppositeImpressionType}_clear_retainer_design_lower_option_for_second_set`] = null;
        return { ...data };
    }

    getRetainerReqFormValuesForDraftCaseSummaryDisplay(
        data,
        retainerRequestId,
        supportedRetainerTypes,
        teethSelections,
        specialInstruction
    ) {
        if (!data) throw new Error('data is required');
        if (!this.impressionType) throw new Error('impressionType is required');

        let baseRetainerRequestFormValues = { ...initRetainerReqFormValue };
        baseRetainerRequestFormValues.impression_type = this.impressionType;
        baseRetainerRequestFormValues.retainer_request_id = retainerRequestId;
        baseRetainerRequestFormValues.special_instruction = specialInstruction || null;
        if (this.impressionType === IMPRESSION_TYPE.UPPER || this.impressionType === IMPRESSION_TYPE.LOWER) {
            const retainerTypeField = data[`${this.impressionType}ArchAndQuantity`];
            const selectedRetainer = supportedRetainerTypes.find(
                (retainer) => retainer.retainer_type === retainerTypeField
            );
            baseRetainerRequestFormValues[`${this.impressionType}_retainer_type_id`] = selectedRetainer
                ? selectedRetainer.retainer_type_id
                : null;
            baseRetainerRequestFormValues[`${this.impressionType}_fixed_wire`] =
                data[`${this.impressionType}FixedWire`] || null;
            baseRetainerRequestFormValues[`${this.impressionType}_bleaching_retainers_select`] =
                data[`${this.impressionType}BleachingRetainersSelect`] || null;
            baseRetainerRequestFormValues[`${this.impressionType}_wire_select`] =
                data[`${this.impressionType}WireSelect`] || null;
            baseRetainerRequestFormValues[`${this.impressionType}_jig_placement_select`] =
                data[`${this.impressionType}JigPlacementSelect`] || null;
            baseRetainerRequestFormValues[`${this.impressionType}_LowTrimlineAreaSelect_3`] =
                data[`${this.impressionType}LowTrimlineAreaSelect_3`] || null;
            baseRetainerRequestFormValues[`${this.impressionType}_LowTrimlineAreaSelect_1`] =
                data[`${this.impressionType}LowTrimlineAreaSelect_1`] || null;
            baseRetainerRequestFormValues[`${this.impressionType}_pontic`] =
                data[`${this.impressionType}Pontic`] || null;
            baseRetainerRequestFormValues[`${this.impressionType}_pontic_color`] =
                data[`${this.impressionType}PonticColor`] || null;
            baseRetainerRequestFormValues[`${this.impressionType}_bite_ramp`] = teethSelections?.toothBiteRamp
                ? [...teethSelections.toothBiteRamp]
                : [];
            baseRetainerRequestFormValues[`${this.impressionType}_clear_retainer_design_upper_option`] =
                data[`${this.impressionType}ClearRetainerDesignUpperOption`] || null;
            baseRetainerRequestFormValues[`${this.impressionType}_clear_retainer_design_upper_option_for_second_set`] =
                data[`${this.impressionType}ClearRetainerDesignUpperOptionForSecondSet`] || null;
            baseRetainerRequestFormValues[`${this.impressionType}_clear_retainer_design_lower_option`] =
                data[`${this.impressionType}ClearRetainerDesignLowerOption`] || null;
            baseRetainerRequestFormValues[`${this.impressionType}_clear_retainer_design_lower_option_for_second_set`] =
                data[`${this.impressionType}ClearRetainerDesignLowerOptionForSecondSet`] || null;
            baseRetainerRequestFormValues =
                this.handleSingleOffTypeRetainerRequestFormValues(baseRetainerRequestFormValues);
        }

        if (this.impressionType === IMPRESSION_TYPE.UPPER_AND_LOWER) {
            const { upperToothBiteRamp, lowerToothBiteRamp } = teethSelections;
            const upperRetainerTypeField = data.upperArchAndQuantity;
            const lowerRetainerTypeField = data.lowerArchAndQuantity;
            const selectedUpperRetainer = supportedRetainerTypes.find(
                (retainer) => retainer.retainer_type === upperRetainerTypeField
            );
            const selectedLowerRetainer = supportedRetainerTypes.find(
                (retainer) => retainer.retainer_type === lowerRetainerTypeField
            );
            baseRetainerRequestFormValues.upper_retainer_type_id = selectedUpperRetainer
                ? selectedUpperRetainer.retainer_type_id
                : null;
            baseRetainerRequestFormValues.lower_retainer_type_id = selectedLowerRetainer
                ? selectedLowerRetainer.retainer_type_id
                : null;

            baseRetainerRequestFormValues[`upper_fixed_wire`] = data[`upperFixedWire`] || null;
            baseRetainerRequestFormValues[`upper_bleaching_retainers_select`] =
                data[`upperBleachingRetainersSelect`] || null;
            baseRetainerRequestFormValues[`upper_wire_select`] = data[`upperWireSelect`] || null;
            baseRetainerRequestFormValues[`upper_jig_placement_select`] = data[`upperJigPlacementSelect`] || null;
            baseRetainerRequestFormValues[`upper_LowTrimlineAreaSelect_3`] = data[`upperLowTrimlineAreaSelect_3`] || null;
            baseRetainerRequestFormValues[`upper_LowTrimlineAreaSelect_1`] = data[`upperLowTrimlineAreaSelect_1`] || null;
            baseRetainerRequestFormValues[`upper_pontic`] = data[`upperPontic`] || null;
            baseRetainerRequestFormValues[`upper_pontic_color`] = data[`upperPonticColor`] || null;
            baseRetainerRequestFormValues[`upper_bite_ramp`] = upperToothBiteRamp ? [...upperToothBiteRamp] : [];

            baseRetainerRequestFormValues[`upper_clear_retainer_design_upper_option`] =
                data[`upperClearRetainerDesignUpperOption`] || null;
            baseRetainerRequestFormValues[`upper_clear_retainer_design_upper_option_for_second_set`] =
                data[`upperClearRetainerDesignUpperOptionForSecondSet`] || null;
            // baseRetainerRequestFormValues[`upper_clear_retainer_design_lower_option`] = data[`upperClearRetainerDesignLowerOption`] || null
            // baseRetainerRequestFormValues[`upper_clear_retainer_design_lower_option_for_second_set`] = data[`upperClearRetainerDesignLowerOptionForSecondSet`] || null

            baseRetainerRequestFormValues[`lower_fixed_wire`] = data[`lowerFixedWire`] || null;
            baseRetainerRequestFormValues[`lower_bleaching_retainers_select`] =
                data[`lowerBleachingRetainersSelect`] || null;
            baseRetainerRequestFormValues[`lower_wire_select`] = data[`lowerWireSelect`] || null;
            baseRetainerRequestFormValues[`lower_jig_placement_select`] = data[`lowerJigPlacementSelect`] || null;
            baseRetainerRequestFormValues[`lower_LowTrimlineAreaSelect_3`] = data[`lowerLowTrimlineAreaSelect_3`] || null;
            baseRetainerRequestFormValues[`lower_LowTrimlineAreaSelect_1`] = data[`lowerLowTrimlineAreaSelect_1`] || null;
            baseRetainerRequestFormValues[`lower_pontic`] = data[`lowerPontic`] || null;
            baseRetainerRequestFormValues[`lower_pontic_color`] = data[`lowerPonticColor`] || null;
            baseRetainerRequestFormValues[`lower_bite_ramp`] = lowerToothBiteRamp ? [...lowerToothBiteRamp] : [];

            // baseRetainerRequestFormValues[`lower_clear_retainer_design_upper_option`] = data[`lowerClearRetainerDesignUpperOption`] || null
            // baseRetainerRequestFormValues[`lower_clear_retainer_design_upper_option_for_second_set`] = data[`lowerClearRetainerDesignUpperOptionForSecondSet`] || null
            baseRetainerRequestFormValues[`lower_clear_retainer_design_lower_option`] =
                data[`lowerClearRetainerDesignLowerOption`] || null;
            baseRetainerRequestFormValues[`lower_clear_retainer_design_lower_option_for_second_set`] =
                data[`lowerClearRetainerDesignLowerOptionForSecondSet`] || null;
        }

        return baseRetainerRequestFormValues;
    }

    /**
     * @type {(files:[StoreFile]) => boolean}
     */
    validateFiles(files) {
        if (!files || !files.length) throw new Error('files is required');
        const MIN_FILES_REQUIRED = 3;
        let valid = false;
        let validFiles = 0;
        for (let index = 0; index < files.length; index++) {
            const file = files[index];
            const platform = file?.platform || null;
            if (platform && [PLATFORM_TYPE.PandaCloud_EN, PLATFORM_TYPE.PandaCloud_EU].includes(platform)) {
                if (file.data && file.uploaded && file.id === 'impression_upper') {
                    return true; // Eletra impression file is valid
                }
            }
            if (file.data) validFiles++;
            if (validFiles >= MIN_FILES_REQUIRED) {
                valid = true;
                break;
            }
        }
        return valid;
    }

    /**
     * @type {(formData, retainerTypeField) => string|null}
     */
    handleWireSelectValue(data, retainerTypeField, impressionType = undefined) {
        let targetSelectValue = null;
        let targetSelectKey = null;
        switch (retainerTypeField) {
            case 'ecodontRetainersWithWire':
                targetSelectKey = 'ecodontWireSelect';
                break;
            case 'MDLSmartFitRetainersWithWire':
                targetSelectKey = 'smartFitWireSelect';
                break;
            case 'fixedPalatalWire':
                targetSelectKey = 'palatalWireSelect';
                break;
            case 'ecodontRetainerWithWire_1set':
                targetSelectKey = 'ecodontRetainerWithWire_1setSelect';
                break;
            default:
                break;
        }
        if (targetSelectKey) {
            if (impressionType) {
                targetSelectKey = `${impressionType}${targetSelectKey.charAt(0).toUpperCase() + targetSelectKey.slice(1)}`;
            }
            targetSelectValue = data[targetSelectKey];
        }
        return targetSelectValue;
    }

    convertFormValueFromNewPatientStoreData(data, supportedRetainerTypes = []) {
        if (!data) throw new Error('data is required');
        if (!supportedRetainerTypes.length) throw new Error('supportedRetainerTypes is required');

        const getArrayDataValue = (list = []) => {
            if (!Array.isArray(list) || list.length === 0 || !list) return null;
            return list;
        };

        const getSelectedRetainer = (retainerTpyeId) => {
            if (!retainerTpyeId) return null;
            const selectedRetainer = supportedRetainerTypes.find(
                (retainer) => retainer.retainer_type_id === retainerTpyeId
            );
            return selectedRetainer ? selectedRetainer.retainer_type : null;
        };

        const getBoolValue = (value) => {
            if (value === null || value === undefined) return null; // Return null for nullish values
            if (typeof value === 'boolean') return value; // Return the boolean value directly
            if (value === 'true' || value === 1 || value === '1') return true; // Handle 'true', 1, and '1'
            if (value === 'false' || value === 0 || value === '0') return false; // Handle 'false', 0, and '0'
            return false; // Default to false for all other cases
        };

        const getBiteRampBool = (target) => {
            const list = getArrayDataValue(target);
            if (Array.isArray(list)) return true;
            return false;
        };

        let upperFixedWireBool = getBoolValue(data.upper_fixed_wire);
        let lowerFixedWireBool = getBoolValue(data.lower_fixed_wire);

        const baseRetainerRequestFormValues = new Map([
            ['impressionType', this.impressionType],
            ['retainer_request_id', data.retainer_request_id || null],
            ['upperFixedWire', upperFixedWireBool || null],
            ['upperArchAndQuantity', getSelectedRetainer(data.upper_retainer_type_id)],
            ['upperBleachingRetainersSelect', data.upper_bleaching_retainers_select || null],

            ['upperWireSelect', data.upper_wire_select || null],
            ['upperJigPlacementSelect', data.upper_jig_placement_select || null],
            ['upperLowTrimlineAreaSelect_3', data.upper_low_trimline_area_select_3 || null],
            ['upperLowTrimlineAreaSelect_1', data.upper_low_trimline_area_select_1 || null],
            ['upperOcclusalBiteRampQuestion', getBiteRampBool(data.upper_bite_ramp)],
            ['upperClearRetainerDesignUpper', data.upperClearRetainerDesignUpper || null],
            ['upperClearRetainerDesignUpperOption', data.upper_clear_retainer_design_upper_option || null],

            [
                'upperClearRetainerDesignUpperForSecondSet',
                data.upper_clear_retainer_design_upper_for_second_set || null,
            ],
            [
                'upperClearRetainerDesignUpperOptionForSecondSet',
                data.upper_clear_retainer_design_upper_option_for_second_set || null,
            ],

            ['upperPontics', getArrayDataValue(data?.upper_pontics)],
            ['upperBiteRamp', getArrayDataValue(data.upper_bite_ramp)],
            ['upperPontic', data.upper_pontic || null],
            ['upperPonticColor', data.upper_pontic_color || null],

            ['lowerFixedWire', lowerFixedWireBool || null],
            ['lowerArchAndQuantity', getSelectedRetainer(data.lower_retainer_type_id)],
            ['lowerBleachingRetainersSelect', data.lower_bleaching_retainers_select || null],

            ['lowerWireSelect', data.lower_wire_select || null],
            ['lowerJigPlacementSelect', data.lower_jig_placement_select || null],
            ['lowerLowTrimlineAreaSelect_3', data.lower_low_trimline_area_select_3 || null],
            ['lowerLowTrimlineAreaSelect_1', data.lower_low_trimline_area_select_1 || null],
            ['lowerOcclusalBiteRampQuestion', getBiteRampBool(data.lower_bite_ramp)],

            ['lowerClearRetainerDesignLower', data.lower_clear_retainer_design_lower || null],
            ['lowerClearRetainerDesignLowerOption', data.lower_clear_retainer_design_lower_option || null],

            [
                'lowerClearRetainerDesignLowerForSecondSet',
                data.lower_clear_retainer_design_lower_for_second_set || null,
            ],
            [
                'lowerClearRetainerDesignLowerOptionForSecondSet',
                data.lower_clear_retainer_design_lower_option_for_second_set || null,
            ],
            ['lowerPontics', getArrayDataValue(data?.lower_pontics)],
            ['lowerBiteRamp', getArrayDataValue(data.lower_bite_ramp)],
            ['lowerPontic', data.lower_pontic || null],
            ['lowerPonticColor', data.lower_pontic_color || null],

            ['specialInstruction', data.special_instruction || null],
        ]);
        return Object.fromEntries(baseRetainerRequestFormValues);
    }
}
